import axios from "axios";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import { getCurrentCurrency, getCurrentTimezone, getIntlLocale, type AppCurrency, type AppLanguage } from "@/i18n/config";
import { translateSystemEnumLabel } from "@/i18n/contentSemantics";
import { translateStaticText } from "@/i18n/messages";
import type { ApiErrorDetail, ApiErrorPayload } from "@/types/api";

const statusColors: Record<string, string> = {
  new: "bg-clinic-info/10 text-clinic-info",
  contacted: "bg-clinic-warning/10 text-clinic-warning",
  scheduled: "bg-clinic-primary/10 text-clinic-primary",
  converted: "bg-clinic-success/10 text-clinic-success",
  lost: "bg-clinic-danger/10 text-clinic-danger",
  completed: "bg-clinic-success/10 text-clinic-success",
  cancelled: "bg-clinic-danger/10 text-clinic-danger",
  rescheduled: "bg-clinic-bg text-clinic-text-secondary",
  no_show: "bg-clinic-accent/10 text-clinic-accent",
  in_progress: "bg-clinic-info/10 text-clinic-info",
  confirmed: "bg-clinic-primary/10 text-clinic-primary",
};

const statusLabels: Record<string, string> = {
  new: "Novo",
  contacted: "Em contato",
  scheduled: "Agendado",
  converted: "Convertido",
  lost: "Não agendado",
  completed: "Concluído",
  cancelled: "Cancelado",
  rescheduled: "Remarcado",
  no_show: "Não compareceu",
  in_progress: "Em andamento",
  confirmed: "Confirmado",
};

const originLabels: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  google: "Google",
  referral: "Indicação",
  website: "Site",
  phone: "Telefone",
  walk_in: "Presencial",
  whatsapp: "WhatsApp",
  linkedin: "LinkedIn",
  other: "Outro",
};

const leadSchedulingOutcomeLabels: Record<string, string> = {
  pending: "Pendente",
  booked: "Marcou consulta",
  not_booked: "Não marcou",
};

const treatmentPlanStatusLabels: Record<string, string> = {
  draft: "Rascunho",
  sold: "Vendido",
  active: "Em execução",
  completed: "Concluído",
  expired: "Vencido",
  cancelled: "Cancelado",
};

const treatmentPlanStatusColors: Record<string, string> = {
  draft: "bg-clinic-text-muted/10 text-clinic-text-muted",
  sold: "bg-clinic-info/10 text-clinic-info",
  active: "bg-clinic-primary/10 text-clinic-primary",
  completed: "bg-clinic-success/10 text-clinic-success",
  expired: "bg-clinic-warning/10 text-clinic-warning",
  cancelled: "bg-clinic-danger/10 text-clinic-danger",
};

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function getApiErrorPayload(error: unknown): ApiErrorPayload | undefined {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  return error.response?.data as ApiErrorPayload | undefined;
}

export function getApiErrorDetail(error: unknown): ApiErrorDetail {
  const responseData = getApiErrorPayload(error);
  return responseData?.detail ?? responseData?.message;
}

function getValidationErrorPath(errorItem: Record<string, unknown>): string {
  const path = errorItem.path;

  if (Array.isArray(path)) {
    return path.map(String).join(".");
  }

  return typeof path === "string" ? path : "";
}

function formatValidationErrorItem(errorItem: Record<string, unknown>): string {
  const path = getValidationErrorPath(errorItem);
  const code = typeof errorItem.code === "string" ? errorItem.code : "";
  const minimum = typeof errorItem.minimum === "number" ? errorItem.minimum : undefined;

  if ((path === "appointmentDuration" || path === "appointment_duration") && code === "too_small" && minimum === 15) {
    return translateStaticText("A duração da consulta deve ser de pelo menos 15 minutos.");
  }

  if (code === "too_small" && typeof minimum === "number") {
    return translateStaticText(`O campo informado precisa ser maior ou igual a ${minimum}.`);
  }

  if (typeof errorItem.msg === "string") {
    return errorItem.msg;
  }

  if (typeof errorItem.message === "string" && !/^Number must be|^String must be|^Invalid/u.test(errorItem.message)) {
    return errorItem.message;
  }

  return translateStaticText("Revise os campos informados e tente novamente.");
}

export function formatApiError(detail: ApiErrorDetail): string {
  if (detail == null) {
    return translateStaticText("Algo deu errado. Tente novamente.");
  }

  if (typeof detail === "string") {
    if (/permiss[aã]o insuficiente|acesso negado/i.test(detail)) {
      return translateStaticText("Você não tem permissão para executar esta ação.");
    }

    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((errorItem) => (errorItem ? formatValidationErrorItem(errorItem) : ""))
      .filter(Boolean)
      .join(" ");
  }

  return formatValidationErrorItem(detail);
}

export function formatCurrency(
  value?: number | null,
  language?: AppLanguage,
  currency: AppCurrency = getCurrentCurrency(),
): string {
  return new Intl.NumberFormat(getIntlLocale(language), {
    style: "currency",
    currency,
  }).format(value ?? 0);
}

export function getCurrencyLabel(
  language?: AppLanguage,
  currency: AppCurrency = getCurrentCurrency(),
): string {
  return new Intl.NumberFormat(getIntlLocale(language), {
    style: "currency",
    currency,
  }).formatToParts(0).find((part) => part.type === "currency")?.value ?? currency;
}

export function getCurrencyFractionDigits(
  language?: AppLanguage,
  currency: AppCurrency = getCurrentCurrency(),
): number {
  return new Intl.NumberFormat(getIntlLocale(language), {
    style: "currency",
    currency,
  }).resolvedOptions().maximumFractionDigits;
}

export function getCurrencyInputStep(
  language?: AppLanguage,
  currency: AppCurrency = getCurrentCurrency(),
): string {
  const fractionDigits = getCurrencyFractionDigits(language, currency);

  if (fractionDigits <= 0) {
    return "1";
  }

  return `0.${"0".repeat(Math.max(fractionDigits - 1, 0))}1`;
}

export function normalizeCurrencyInputValue(
  value: string,
  language?: AppLanguage,
  currency: AppCurrency = getCurrentCurrency(),
): string {
  const fractionDigits = getCurrencyFractionDigits(language, currency);
  const sanitized = value.replace(/[^\d.,]/g, "");

  if (!sanitized) {
    return "";
  }

  const lastComma = sanitized.lastIndexOf(",");
  const lastDot = sanitized.lastIndexOf(".");
  const decimalIndex = Math.max(lastComma, lastDot);

  if (fractionDigits <= 0) {
    const integerValue = (decimalIndex >= 0 ? sanitized.slice(0, decimalIndex) : sanitized).replace(/\D/g, "");
    return integerValue;
  }

  if (decimalIndex < 0) {
    return sanitized.replace(/\D/g, "");
  }

  const integerPart = sanitized.slice(0, decimalIndex).replace(/\D/g, "") || "0";
  const decimalPart = sanitized.slice(decimalIndex + 1).replace(/\D/g, "").slice(0, fractionDigits);

  return `${integerPart}.${decimalPart}`;
}

export function formatDate(dateValue?: string | null, language?: AppLanguage): string {
  if (!dateValue) {
    return "-";
  }

  const isCivilDate = /^\d{4}-\d{2}-\d{2}$/.test(dateValue);
  return new Intl.DateTimeFormat(getIntlLocale(language), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: isCivilDate ? "UTC" : getCurrentTimezone(),
  }).format(new Date(isCivilDate ? `${dateValue}T00:00:00.000Z` : dateValue));
}

export function formatDateTime(dateValue?: string | null, language?: AppLanguage): string {
  if (!dateValue) {
    return "-";
  }

  return new Intl.DateTimeFormat(getIntlLocale(language), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: getCurrentTimezone(),
  }).format(new Date(dateValue));
}

export function formatTime(dateValue?: string | null): string {
  if (!dateValue) {
    return "-";
  }

  return new Intl.DateTimeFormat(getIntlLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: getCurrentTimezone(),
  }).format(new Date(dateValue));
}

export function formatPercent(value?: number | null, language?: AppLanguage): string {
  return new Intl.NumberFormat(getIntlLocale(language), {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format((value ?? 0) / 100);
}

export function formatBytes(value?: number | null, language?: AppLanguage): string {
  const bytes = Math.max(value ?? 0, 0);
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  let normalizedValue = bytes;

  while (normalizedValue >= 1024 && unitIndex < units.length - 1) {
    normalizedValue /= 1024;
    unitIndex += 1;
  }

  const maximumFractionDigits = normalizedValue >= 10 || unitIndex === 0 ? 0 : 1;

  return `${new Intl.NumberFormat(getIntlLocale(language), {
    maximumFractionDigits,
  }).format(normalizedValue)} ${units[unitIndex]}`;
}

export function getStatusColor(status?: string | null): string {
  if (!status) {
    return "bg-gray-100 text-gray-600";
  }

  return statusColors[status] ?? "bg-gray-100 text-gray-600";
}

export function getStatusLabel(status?: string | null, language?: AppLanguage): string {
  const labels = {
    new: "Novo",
    contacted: "Em Contato",
    scheduled: "Agendado",
    converted: "Convertido",
    lost: "Não agendado",
    completed: "Concluído",
    cancelled: "Cancelado",
    rescheduled: "Remarcado",
    no_show: "Não Compareceu",
    in_progress: "Em Andamento",
    confirmed: "Confirmado",
  };
  if (!status) {
    return "-";
  }

  return translateSystemEnumLabel(status, labels, language);
}

export function getOriginLabel(origin?: string | null, language?: AppLanguage): string {
  const labels = {
    instagram: "Instagram",
    facebook: "Facebook",
    google: "Google",
    referral: "Indicação",
    website: "Site",
    phone: "Telefone",
    walk_in: "Presencial",
    whatsapp: "WhatsApp",
    linkedin: "LinkedIn",
    other: "Outro",
  };
  if (!origin) {
    return "-";
  }

  return translateSystemEnumLabel(origin, labels, language);
}

export function getLeadSchedulingOutcomeLabel(value?: string | null, language?: AppLanguage): string {
  if (!value) {
    return "-";
  }

  return translateSystemEnumLabel(value, leadSchedulingOutcomeLabels, language);
}

export function getTreatmentPlanStatusLabel(value?: string | null, language?: AppLanguage): string {
  if (!value) {
    return "-";
  }

  return translateSystemEnumLabel(value, treatmentPlanStatusLabels, language);
}

export function getTreatmentPlanStatusColor(value?: string | null): string {
  if (!value) {
    return "bg-gray-100 text-gray-600";
  }

  return treatmentPlanStatusColors[value] || "bg-gray-100 text-gray-600";
}
